import { AxiosError } from 'axios';
import i18next from 'i18next';
import { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  configQueryKeys,
  useConfigAgencyFetchQuery,
  useConfigAtlasFetchQuery,
  useConfigDeveloperFetchQuery,
  useConfigLandlordFetchQuery,
} from '@apis/configApi';
import { queryClient } from '@core/http-client';
import { configActions } from '@redux/reducers/config/configReducer';
import { RootState } from '@redux/reducers/rootReducer';
import { ConfigErrorResponse } from '@shared/apis/configApi.types';
import { Loading } from '@shared/components/loading';
import { showNotification } from '@shared/components/notification';
import {
  ConfigAgency,
  ConfigAgencyConstants,
  ConfigAtlasConstants,
  ConfigCore,
  ConfigDeveloper,
  ConfigDeveloperConstants,
  ConfigLandlord,
  ConfigLandlordConstants,
} from '@shared/models/config';
import { getDefaultError } from '@shared/utils/common';

export enum AppId {
  Agency,
  Atlas,
  Developer,
  Landlord,
}

type Configs = ConfigAgency | ConfigLandlord;
type Constants = ConfigAgencyConstants | ConfigAtlasConstants | ConfigLandlordConstants;

const ALERTS_DISMISSED_KEY = 'alertsDismissed';

type AlertsDismissed = Record<string, boolean>;

export interface ConfigContextValue {
  alertsDismissed: AlertsDismissed;
  appId: AppId;
  config: ConfigCore;
  constants: Constants;
  error: string;
  sideNavCollapsed: boolean;
  setAlertDismissed: (key: string) => void;
  setSideNavCollapsed: UseStateSetter<boolean>;
}

export const ConfigContext = createContext<ConfigContextValue>({} as ConfigContextValue);

export const useConfigAll = () => useContext(ConfigContext);

export const useConfig = () => useContext(ConfigContext).config;

export const getAppUrl = (appId: AppId, config: ConfigCore) => {
  switch (appId) {
    case AppId.Agency:
      return config.appUrl;
    case AppId.Atlas:
      return config.atlasAppUrl;
    case AppId.Developer:
      return config.developerAppUrl;
    case AppId.Landlord:
      return config.landlordAppUrl;
  }
};

export const useAppUrl = () => {
  const { appId, config } = useConfigAll();

  return { appUrl: getAppUrl(appId, config) };
};

// Strict agency constants
export const useAgencyConstants = () => {
  return { constants: useContext(ConfigContext).constants as ConfigAgencyConstants };
};

// Strict landlord constants
export const useLandlordConstants = () => {
  return { constants: useContext(ConfigContext).constants as ConfigLandlordConstants };
};

// Strict atlas constants
export const useAtlasConstants = () => {
  return { constants: useContext(ConfigContext).constants as ConfigAtlasConstants };
};

// Strict developer constants
export const useDeveloperConstants = () => {
  return { constants: useContext(ConfigContext).constants as ConfigDeveloperConstants };
};

// Developer config accessor (since ConfigDeveloper has a different structure than ConfigCore)
export const useDeveloperConfig = () => {
  const context = useContext(ConfigContext);
  const configWithConstants = window.AS as ConfigDeveloper;
  return {
    config: {
      ...configWithConstants,
      constants: context.constants as ConfigDeveloperConstants,
    } as ConfigDeveloper,
    constants: context.constants as ConfigDeveloperConstants,
  };
};

export interface ConfigProviderProps {
  appId: AppId;
}

export const ConfigProvider: FC<ConfigProviderProps> = ({ appId, children }) => {
  const dispatch = useDispatch();

  // Legacy stuff
  const reduxUser = useSelector((state: RootState) => state.config.user);

  const [alertsDismissed, setAlertsDismissed] = useState<AlertsDismissed>({});
  const [sideNavCollapsed, setSideNavCollapsed] = useState(false);

  const successHandler = useCallback((config: Configs) => {
    window.AS = config;

    i18next.addResources('en', 'core', config.constants.localisation.translations);

    const localDismissedStr = localStorage.getItem(`${ALERTS_DISMISSED_KEY}_${config.user.id}`);
    setAlertsDismissed(localDismissedStr ? JSON.parse(localDismissedStr) : {});

    if ('appUrl' in config && 'constants' in config && 'user' in config && 'teams' in config) {
      dispatch(configActions.setUser(config.user));
    }
  }, []);

  const developerSuccessHandler = useCallback((config: ConfigDeveloper) => {
    window.AS = config;

    i18next.addResources('en', 'core', config.constants.localisation.translations);

    const localDismissedStr = localStorage.getItem(`${ALERTS_DISMISSED_KEY}_${config.user.id}`);
    setAlertsDismissed(localDismissedStr ? JSON.parse(localDismissedStr) : {});
  }, []);

  const errorHandler = useCallback((error: AxiosError<ConfigErrorResponse>) => {
    if (error.response?.status === 403 && error.response?.data) {
      window.location.href = error.response.data.redirect_url;
    } else {
      showNotification(getDefaultError(), 'error');
    }
  }, []);

  const configAgencyQuery = useConfigAgencyFetchQuery({
    enabled: appId === AppId.Agency,
    onSuccess: successHandler,
    onError: errorHandler,
  });

  const configAtlasQuery = useConfigAtlasFetchQuery({
    enabled: appId === AppId.Atlas,
    onSuccess: successHandler,
    onError: errorHandler,
  });

  const configDeveloperQuery = useConfigDeveloperFetchQuery({
    enabled: appId === AppId.Developer,
    onSuccess: developerSuccessHandler,
    onError: errorHandler,
  });

  const configLandlordQuery = useConfigLandlordFetchQuery({
    enabled: appId === AppId.Landlord,
    onSuccess: successHandler,
    onError: errorHandler,
  });

  useEffect(() => {
    if (!reduxUser) {
      return;
    }

    if (appId === AppId.Agency && configAgencyQuery.isSuccess) {
      queryClient.setQueryData(configQueryKeys.showAgency().queryKey, { ...configAgencyQuery.data, user: reduxUser });
    }
    if (appId === AppId.Landlord && configLandlordQuery.isSuccess) {
      queryClient.setQueryData(configQueryKeys.showLandlord().queryKey, {
        ...configLandlordQuery.data,
        user: reduxUser,
      });
    }
    if (appId === AppId.Atlas && configAtlasQuery.isSuccess) {
      queryClient.setQueryData(configQueryKeys.showAtlas().queryKey, {
        ...configAtlasQuery.data,
        user: reduxUser,
      });
    }
  }, [appId, reduxUser]);

  const contextValue = useMemo(() => {
    let configs = {} as Configs;
    if (appId === AppId.Agency && configAgencyQuery.isSuccess) {
      configs = configAgencyQuery.data;
    } else if (appId === AppId.Landlord && configLandlordQuery.isSuccess) {
      configs = configLandlordQuery.data;
    } else if (appId === AppId.Atlas && configAtlasQuery.isSuccess) {
      configs = configAtlasQuery.data;
    }

    let errorMessage = '';
    if (appId === AppId.Agency && configAgencyQuery.isError) {
      errorMessage = configAgencyQuery.error?.message;
    } else if (appId === AppId.Developer && configDeveloperQuery.isError) {
      errorMessage = configDeveloperQuery.error?.message;
    } else if (appId === AppId.Landlord && configLandlordQuery.isError) {
      errorMessage = configLandlordQuery.error?.message;
    } else if (appId === AppId.Atlas && configAtlasQuery.isError) {
      errorMessage = configAtlasQuery.error?.message;
    }

    // Developer config has a different structure - access via useDeveloperConfig hook
    if (appId === AppId.Developer && configDeveloperQuery.isSuccess) {
      const developerConfig = configDeveloperQuery.data;
      return {
        appId,
        alertsDismissed,
        config: {} as ConfigCore,
        constants: developerConfig.constants as unknown as Constants,
        error: errorMessage,
        sideNavCollapsed,
        setAlertDismissed: (key: string) => {
          setAlertsDismissed((prev) => {
            const result = { ...prev };
            result[key] = true;
            localStorage.setItem(`${ALERTS_DISMISSED_KEY}_${developerConfig.user.id}`, JSON.stringify(result));
            return result;
          });
        },
        setSideNavCollapsed,
      } satisfies ConfigContextValue;
    }

    const { constants, ...config } = configs;

    return {
      appId,
      alertsDismissed,
      config,
      constants,
      error: errorMessage,
      sideNavCollapsed,
      setAlertDismissed: (key: string) => {
        setAlertsDismissed((prev) => {
          const result = { ...prev };

          // Dismiss the alert
          result[key] = true;

          // Update local storage state
          localStorage.setItem(`${ALERTS_DISMISSED_KEY}_${config.user.id}`, JSON.stringify(result));

          return result;
        });
      },
      setSideNavCollapsed,
    } satisfies ConfigContextValue;
  }, [
    alertsDismissed,
    appId,
    configAgencyQuery.data,
    configAgencyQuery.error,
    configAgencyQuery.isError,
    configAgencyQuery.isSuccess,
    configAtlasQuery.data,
    configAtlasQuery.error,
    configAtlasQuery.isError,
    configAtlasQuery.isSuccess,
    configDeveloperQuery.data,
    configDeveloperQuery.error,
    configDeveloperQuery.isError,
    configDeveloperQuery.isSuccess,
    configLandlordQuery.data,
    configLandlordQuery.error,
    configLandlordQuery.isError,
    configLandlordQuery.isSuccess,
    sideNavCollapsed,
  ]);

  if (
    configAgencyQuery.isFetching ||
    configAtlasQuery.isFetching ||
    configDeveloperQuery.isFetching ||
    configLandlordQuery.isFetching
  ) {
    return <Loading absolute />;
  }

  return <ConfigContext.Provider value={contextValue}>{children}</ConfigContext.Provider>;
};
